import React, { ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  enabled?: boolean;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  children,
  enabled = true,
}) => {
  const { containerRef, isPulling, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh,
    enabled,
  });

  // Calculate rotation based on pull distance (0-180 degrees)
  const rotation = Math.min((pullDistance / 80) * 180, 180);
  const opacity = Math.min(pullDistance / 80, 1);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        touchAction: 'pan-y',
      }}
    >
      {/* Pull-to-refresh indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: Math.min(pullDistance, 80),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 0.5,
          opacity: opacity,
          transition: isRefreshing || !isPulling ? 'all 0.3s ease-out' : 'none',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
        }}
      >
        {isRefreshing ? (
          <>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary">
              Refreshing...
            </Typography>
          </>
        ) : (
          <>
            <Refresh
              sx={{
                fontSize: 32,
                color: 'primary.main',
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.1s ease-out',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {pullDistance > 80 ? 'Release to refresh' : 'Pull to refresh'}
            </Typography>
          </>
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          transform: isPulling || isRefreshing ? `translateY(${Math.min(pullDistance, 80)}px)` : 'none',
          transition: isRefreshing || !isPulling ? 'transform 0.3s ease-out' : 'none',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
